// This is PoC code
import { EmojiEvents } from '@mui/icons-material';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useCurrency } from '@procureai/ui-mui';
import { Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { SvbrgQuoteComparatorOutput, SvbrgQuoteTermsType } from '@/generated/gql/graphql';
import { NULLISH_VALUE } from '~/common/constants';
import { I18nNamespace } from '~/common/types';

export type QuoteComparisonResultProps = {
  result: SvbrgQuoteComparatorOutput;
};

export const QuoteComparisonResult = ({ result }: QuoteComparisonResultProps): ReactNode => {
  const { bestOption, rationale, lineItemGroups, quotes } = result;
  const { format } = useCurrency();
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'QuoteComparisonResult' });

  // Sort quotes to put the best option first
  const sortedQuotes = [...quotes].sort((a, b) => {
    if (a.fileId === bestOption) return -1;
    if (b.fileId === bestOption) return 1;
    return 0;
  });

  // Helper to get term value by type
  const getTermValue = (quote: (typeof quotes)[0], termType: SvbrgQuoteTermsType) => {
    const term = quote.quote.terms.find((t) => t.type === termType);
    return term?.value || NULLISH_VALUE;
  };

  // Helper to get line item for a specific quote
  const getLineItemForQuote = (quote: (typeof quotes)[0], lineItemName: string) => {
    return quote.quote.lineItems.find((item) => item.name === lineItemName);
  };

  // Get all unique term types across all quotes
  const allTermTypes = Array.from(new Set(quotes.flatMap((quote) => quote.quote.terms.map((term) => term.type))));

  return (
    <Box sx={{ mt: 2 }}>
      {/* Rationale */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          <strong>{t('recommendation')}</strong> {rationale}
        </Typography>
      </Box>

      {/* Comparison Table */}
      <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 200 }}>{t('supplier')}</TableCell>
              {sortedQuotes.map((quote) => (
                <TableCell key={quote.fileId} align="center" sx={{ fontWeight: 600 }}>
                  {quote.quote.supplier}
                  {quote.fileId === bestOption && (
                    <EmojiEvents color="warning" fontSize="small" sx={{ ml: 1, verticalAlign: 'middle' }} />
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Date Row */}
            <TableRow>
              <TableCell>{t('date')}</TableCell>
              {sortedQuotes.map((quote) => (
                <TableCell key={quote.fileId} align="center">
                  {quote.quote.date}
                </TableCell>
              ))}
            </TableRow>

            {/* Compliance Row - Placeholder for future feature */}
            <TableRow>
              <TableCell>{t('compliance')}</TableCell>
              {sortedQuotes.map((quote) => (
                <TableCell key={quote.fileId} align="center">
                  -
                </TableCell>
              ))}
            </TableRow>

            {/* Total Price Row */}
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>{t('totalPrice')}</TableCell>
              {sortedQuotes.map((quote) => (
                <TableCell key={quote.fileId} align="center" sx={{ fontWeight: 600 }}>
                  {format(quote.quote.totalPrice)}
                </TableCell>
              ))}
            </TableRow>

            {/* Line Items Section */}
            <TableRow>
              <TableCell colSpan={sortedQuotes.length + 1} sx={{ fontWeight: 600, backgroundColor: 'grey.100', py: 1 }}>
                {t('lineItems')}
              </TableCell>
            </TableRow>

            {/* Grouped Line Items */}
            {lineItemGroups.map((group, groupIndex) => (
              <Fragment key={`group-${groupIndex}`}>
                {/* Group Header */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, pl: 3 }}>{group.lineItemGroup}</TableCell>
                  {sortedQuotes.map((quote) => (
                    <TableCell key={quote.fileId} />
                  ))}
                </TableRow>

                {/* Group Items */}
                {group.lineItems.map((lineItemName, itemIndex) => (
                  <TableRow key={`item-${groupIndex}-${itemIndex}`}>
                    <TableCell sx={{ pl: 5 }}>{lineItemName}</TableCell>
                    {sortedQuotes.map((quote) => {
                      const item = getLineItemForQuote(quote, lineItemName);
                      return (
                        <TableCell key={quote.fileId} align="center">
                          {item
                            ? t('lineItemDisplay', {
                                quantity: item.quantity,
                                unit: item.unit,
                                unitPrice: format(item.unitPrice),
                                totalPrice: format(item.totalPrice),
                              })
                            : '-'}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </Fragment>
            ))}

            {/* Terms & Conditions Section - Only show if there are terms */}
            {allTermTypes.length > 0 && (
              <>
                <TableRow>
                  <TableCell
                    colSpan={sortedQuotes.length + 1}
                    sx={{ fontWeight: 600, backgroundColor: 'grey.100', py: 1 }}
                  >
                    {t('termsConditions')}
                  </TableCell>
                </TableRow>

                {/* Dynamic Terms Rows */}
                {allTermTypes.map((termType) => (
                  <TableRow key={termType}>
                    <TableCell sx={{ pl: 3 }}>{t(termType)}</TableCell>
                    {sortedQuotes.map((quote) => (
                      <TableCell key={quote.fileId} align="center">
                        {getTermValue(quote, termType)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
